// pages/Staff.js
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../components/TopBar";
import Header from "../components/Header";
import PreFooter from "../components/PreFooter";
import Footer from "../components/Footer";
import { staffMembers } from "../data/staffData";

function Staff() {
  const navigate = useNavigate();
  const introRef = useRef(null);
  const labelRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          if (el.classList.contains("js-animate")) {
            el.classList.add("animate-in");
          }
          if (el.classList.contains("js-card")) {
            const siblings = [...el.parentElement.querySelectorAll(".js-card")];
            const idx = siblings.indexOf(el);
            setTimeout(() => el.classList.add("animate-in"), idx * 130);
          }
          io.unobserve(el);
        });
      },
      { threshold: 0.12 }
    );

    if (introRef.current) io.observe(introRef.current);
    if (labelRef.current) io.observe(labelRef.current);
    cardRefs.current.forEach((card) => { if (card) io.observe(card); });

    return () => io.disconnect();
  }, []);

  return (
    <>
      <TopBar />
      <Header activePage="about" />

      {/* ── Page Hero with background photo ── */}
      <section className="page-hero staff-hero" style={heroSectionStyle}>

        {/* Background photo */}
        <div style={heroBgStyle}></div>

        {/* Dark maroon gradient overlay */}
        <div style={heroOverlayStyle}></div>

        {/* Decorative diamond pattern */}
        <div className="hero-pattern" style={{ zIndex: 2 }}>
          {Array.from({ length: 32 }).map((_, i) => <span key={i}></span>)}
        </div>

        {/* Hero text — sits above all layers */}
        <div className="hero-text-block" style={{ position: "relative", zIndex: 3 }}>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span className="breadcrumb-sep">›</span>
            <span>About</span>
            <span className="breadcrumb-sep">›</span>
            <span>Staff</span>
          </div>
          <h2>Meet Our <span>Staff</span></h2>
          <div className="hero-rule"></div>
          <p>The dedicated professionals powering the Media &amp; Community Relations Centre</p>
        </div>
      </section>

      {/* Intro Strip */}
      <div className="intro-strip">
        <div className="intro-inner js-animate" ref={introRef}>
          <p>
            Our <strong>staff members</strong> bring expertise in media production, web technology,
            and audio-visual services to the <strong>University of Peradeniya</strong> community,
            supporting every event, broadcast, and digital initiative of the Centre.
          </p>
        </div>
      </div>

      {/* Staff Section */}
      <section className="staff-section" id="staff">
        <div className="staff-content">

          <div className="section-label js-animate" ref={labelRef}>
            <p className="eyebrow">Our Team</p>
            <h3>The People Behind the Centre</h3>
          </div>

          <div className="staff-grid">
            {staffMembers.map((member, index) => (
              <div
                key={member.id}
                className="staff-card js-card staff-card--clickable"
                ref={(el) => (cardRefs.current[index] = el)}
                onClick={() => navigate(`/staff/${member.slug}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate(`/staff/${member.slug}`); }}
                aria-label={`View profile of ${member.name}`}
              >
                <div className={`card-stripe ${member.stripeClass}`}></div>

                {/* Photo or placeholder */}
                <div className="card-photo-wrap">
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt={member.photoAlt}
                      style={{ objectPosition: member.photoPosition || "center" }}
                    />
                  ) : (
                    <div className="card-photo-placeholder">
                      <span className="placeholder-icon">👤</span>
                    </div>
                  )}
                </div>

                <div className="card-body">
                  <h2 className="card-name">{member.name}</h2>
                  <p className="card-role">{member.role}</p>
                  <div className="card-divider"></div>
                  <div className="card-meta">
                    <div className="meta-item">
                      <span className="meta-icon">&#127970;</span>
                      <span>Media &amp; Community Relations Centre</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-icon">&#127979;</span>
                      <span>University of Peradeniya</span>
                    </div>
                  </div>
                  <div className="card-view-profile">
                    <span>View Profile</span>
                    <span className="view-arrow">→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      <PreFooter />
      <Footer />
    </>
  );
}

export default Staff;

/* ─── Hero inline styles ─── */

/* Let the CSS .staff-hero handle height/display,
   we only add position:relative so absolute children work */
const heroSectionStyle = {
  position: "relative",
};

/* Background photo — behind everything */
const heroBgStyle = {
  position: "absolute",
  inset: 0,
  backgroundImage: "url('/staff-team.jpg')",
  backgroundSize: "cover",
  display: "block",
  backgroundPosition: "center 30%",  // show upper portion of the photo
  zIndex: 0,
};

/* Maroon gradient overlay matching the screenshot's left-dark → right-lighter look */
const heroOverlayStyle = {
  position: "absolute",
  inset: 0,
  background:
    "linear-gradient(135deg, rgba(50,0,0,0.90) 0%, rgba(128,0,0,0.80) 50%, rgba(80,0,0,0.88) 100%)",
  zIndex: 1,
};